/**
 * Preloading utilities for lazy-loaded components
 * Helps improve perceived performance by loading components before they're needed
 */

// Preload functions for critical routes
export const preloadComponents = {
  // Authentication flow components
  login: () => import('@components/auth/LoginPage'),
  signup: () => import('@components/auth/SignupPage'),
  dashboard: () => import('@components/auth/DashboardPage'),

  // Core app components
  offerForm: () => import('@components/offer/OfferForm'),
  offerView: () => import('@components/offer/ConsolidatedOfferView'),

  // Company management
  companySettings: () => import('@components/company/CompanySettingsPage'),
  companyBranding: () => import('@components/company/CompanyBrandingPage'),
  salaryEquityBands: () => import('@components/company/SalaryEquityBandsPage'),
  internalEquity: () => import('@components/company/InternalEquityPage'),

  // User settings
  userSettings: () => import('@/components/settings/UserSettingsPage'),
};

/**
 * Preload components on user interaction (hover, focus)
 * @param componentKey - Key from preloadComponents object
 */
export const preloadOnInteraction = (componentKey: keyof typeof preloadComponents) => {
  const preloadFn = preloadComponents[componentKey];
  if (preloadFn) {
    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => preloadFn());
    } else {
      setTimeout(() => preloadFn(), 100);
    }
  }
};

/**
 * Preload critical components after the initial page load
 * This runs during idle time to prepare for likely user actions
 */
export const preloadCriticalComponents = () => {
  // Wait for the initial page to be fully loaded
  if ('requestIdleCallback' in window) {
    requestIdleCallback(
      () => {
        // Preload most likely next routes based on current route
        const currentPath = window.location.pathname;

        if (currentPath === '/' || currentPath === '/login' || currentPath === '/signup') {
          // For public pages, preload dashboard and login/signup
          preloadComponents.dashboard();
          if (currentPath !== '/login') preloadComponents.login();
          if (currentPath !== '/signup') preloadComponents.signup();
        } else if (currentPath === '/dashboard') {
          // For dashboard, preload common next actions
          preloadComponents.offerForm();
          preloadComponents.companySettings();
        }
      },
      { timeout: 3000 }
    );
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      const currentPath = window.location.pathname;

      if (currentPath === '/' || currentPath === '/login' || currentPath === '/signup') {
        preloadComponents.dashboard();
      } else if (currentPath === '/dashboard') {
        preloadComponents.offerForm();
      }
    }, 2000);
  }
};

/**
 * Preload components based on mouse movement towards navigation links
 * This implements predictive preloading
 */
export const initPredictivePreloading = () => {
  // Map of route patterns to preload functions
  const routePreloadMap: Record<string, keyof typeof preloadComponents> = {
    '/login': 'login',
    '/signup': 'signup',
    '/dashboard': 'dashboard',
    '/offers/create': 'offerForm',
    '/company/settings': 'companySettings',
    '/company/branding': 'companyBranding',
    '/company/salary-equity-bands': 'salaryEquityBands',
    '/company/internal-equity': 'internalEquity',
    '/user-settings': 'userSettings',
  };

  // Add event listeners to navigation links
  document.addEventListener('mouseover', event => {
    const target = event.target as HTMLElement;
    const link = target.closest('a[href]') as HTMLAnchorElement;

    if (link && link.href) {
      const url = new URL(link.href);
      const pathname = url.pathname;

      // Check if we have a preload function for this route
      const preloadKey = routePreloadMap[pathname];
      if (preloadKey) {
        preloadOnInteraction(preloadKey);
      }
    }
  });

  // Also preload on focus for keyboard users
  document.addEventListener('focusin', event => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      const link = target as HTMLAnchorElement;
      if (link.href) {
        const url = new URL(link.href);
        const pathname = url.pathname;

        const preloadKey = routePreloadMap[pathname];
        if (preloadKey) {
          preloadOnInteraction(preloadKey);
        }
      }
    }
  });
};

/**
 * Initialize all preloading strategies
 * Call this once after the app has loaded
 */
export const initPreloading = () => {
  // Preload critical components during idle time
  preloadCriticalComponents();

  // Set up predictive preloading
  initPredictivePreloading();
};
